import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import type { TemplateField, SectionFieldOption } from 'views/App/Portal/PerformanceReporting/Templates/_shared/_types';
import type { FieldTypeEnum } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { parseTimeValue } from 'views/App/Portal/PerformanceReporting/_utils/fieldInputHelpers';
import BaseDateInput from './BaseDateInput';
import BaseNumberInput from './BaseNumberInput';
import BaseTimeInput from './BaseTimeInput';
import BaseSelectInput from './BaseSelectInput';
import BaseAttachmentDisplay from './BaseAttachmentDisplay';
import TabbedImageDisplay from './TabbedImageDisplay';
import { BODY_IMAGE_TABS, VEHICLE_IMAGE_TABS } from './imageDisplayConfigs';
import { translate as t } from 'hooks/useTranslations';

export interface FieldTypeDisplayProps {
  field: TemplateField;
  fieldType?: FieldTypeEnum;
  disabled?: boolean;
  isBuilderCard?: boolean;
  size?: 'small' | 'medium';
  onChange?: (values: string[]) => void;
}

const isValidOption = (opt: SectionFieldOption): boolean => {
  return opt.status === 'Active' && !opt.effectiveIsDeleted && !!opt.effectiveLabel && opt.effectiveLabel.trim() !== '';
};

const getSelectOptions = (field: TemplateField): { value: string; label: string }[] => {
  if (!field.sectionFieldOptions) return [];
  return field.sectionFieldOptions.filter(isValidOption).map((opt) => ({
    value: opt.effectiveLabel,
    label: opt.effectiveLabel,
  }));
};

// Removes the theme's MuiFormControl paddingTop/backgroundColor when there is no label
const NO_LABEL_SX = {
  '& .MuiFormControl-root': { paddingTop: 0, backgroundColor: 'transparent' },
  '& .MuiTextField-root': { paddingTop: 0, backgroundColor: 'transparent' },
};

const FieldTypeDisplay: React.FC<FieldTypeDisplayProps> = ({
  field,
  fieldType: fieldTypeProp,
  disabled = false,
  isBuilderCard = false,
  size = 'medium',
  onChange,
}) => {
  const fieldType = fieldTypeProp ?? field.fieldType ?? 'Text';
  const valueArr = field.effectiveDefaultValues ?? [];
  const value = valueArr[0] ?? ''; // single-value display for most types
  const label = !isBuilderCard ? (field.effectiveLabel || field.name) : undefined;
  const inputSize = size === 'small' ? 'small' : 'medium';
  const noop = () => {};
  // In the template builder card, disable all inputs except SingleSelect
  // so users can see the value but can't interact. SingleSelect keeps its normal look
  // since react-select disabled is very visually intrusive; onChange is already a noop.
  const fieldDisabled = isBuilderCard || disabled;

  const renderInput = (): React.ReactNode => {
    switch (fieldType) {
      case 'Text':
      case 'Lookup':
        return (
          <TextField
            label={label}
            value={value}
            onChange={(e) => (onChange ?? noop)([e.target.value])}
            disabled={fieldDisabled}
            fullWidth
            size={inputSize}
            variant="outlined"
          />
        );

      case 'Number':
        return (
          <BaseNumberInput
            value={value}
            onChange={(v: string) => (onChange ?? noop)([v])}
            label={label}
            size={inputSize}
            fullWidth={true}
            disabled={fieldDisabled}
          />
        );

      case 'Date':
        return (
          <BaseDateInput
            value={value}
            onChange={(v: string) => (onChange ?? noop)([v])}
            label={label ?? ''}
            size={inputSize}
            fullWidth={true}
            disabled={fieldDisabled}
          />
        );

      case 'Time': {
        const timeValue = value ? parseTimeValue(value) : null;
        return (
          <BaseTimeInput
            value={timeValue}
            onChange={(v) => (onChange ?? noop)(v ? [v.format('HH:mm')] : [])}
            label={label ?? ''}
            size={inputSize}
            fullWidth={true}
            showTimezone={!isBuilderCard}
            disabled={fieldDisabled}
          />
        );
      }

      case 'Boolean': {
        const booleanValue = value === 'true' || value === 'True';
        return (
          <FormControl fullWidth disabled={fieldDisabled}>
            {label && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {label}
              </Typography>
            )}
            <RadioGroup value={booleanValue ? 'yes' : 'no'} row onChange={(e) => (onChange ?? noop)([(e.target as HTMLInputElement).value === 'yes' ? 'true' : 'false'])}>
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        );
      }

      case 'SingleSelect': {
        const options = getSelectOptions(field);
        const select = (
          <BaseSelectInput
            options={options}
            value={value}
            onChange={(v) => (onChange ?? noop)(Array.isArray(v) ? v : (v ? [v] : []))}
            isMulti={false}
            label={label}
            placeholder="Select..."
            disabled={disabled}
            size={inputSize}
            fullWidth={true}
            hideLabel={isBuilderCard}
            isSearchable={!isBuilderCard}
            isOptionDisabled={isBuilderCard ? () => true : undefined}
          />
        );
        return isBuilderCard ? (
          <Box onClick={(e) => e.stopPropagation()}>{select}</Box>
        ) : select;
      }
      case 'MultiSelect': {
        const options = getSelectOptions(field);
        const select = (
          <BaseSelectInput
            options={options}
            value={valueArr}
            onChange={(v) => (onChange ?? noop)(Array.isArray(v) ? v : (v ? [v] : []))}
            isMulti={true}
            label={label}
            placeholder="Select..."
            disabled={disabled}
            size={inputSize}
            fullWidth={true}
            hideLabel={isBuilderCard}
            isSearchable={!isBuilderCard}
            isOptionDisabled={isBuilderCard ? () => true : undefined}
          />
        );
        return isBuilderCard ? (
          <Box onClick={(e) => e.stopPropagation()}>{select}</Box>
        ) : select;
      }

      case 'Attachment':
        return <BaseAttachmentDisplay />;
      case 'BodyImage':
        return (
          <TabbedImageDisplay
            tabs={BODY_IMAGE_TABS}
            instructionText={t('performanceReporting.fieldBank.tabbedImageDisplay.bodyInstruction')}
          />
        );
      case 'VehicleImage':
        return (
          <TabbedImageDisplay
            tabs={VEHICLE_IMAGE_TABS}
            instructionText={t('performanceReporting.fieldBank.tabbedImageDisplay.vehicleInstruction')}
            imageHeight={250}
          />
        );

      case 'Narrative':
        return (
          <TextField
            label={label}
            value={value}
            onChange={(e) => (onChange ?? noop)([e.target.value])}
            disabled={fieldDisabled}
            fullWidth
            size={inputSize}
            variant="outlined"
            multiline
            minRows={3}
          />
        );

      default:
        return (
          <TextField
            label={label}
            value={value}
            onChange={(e) => (onChange ?? noop)([e.target.value])}
            disabled={fieldDisabled}
            fullWidth
            size={inputSize}
            variant="outlined"
          />
        );
    }
  };

  if (!isBuilderCard) {
    return <>{renderInput()}</>;
  }

  return <Box sx={NO_LABEL_SX}>{renderInput()}</Box>;
};

export default FieldTypeDisplay;
